<template>
  <form class="search-form" @submit.prevent="onSubmit">
    <div class="row mb-3">
      <div class="col-12">
        <div class="d-flex align-items-center gap-2">
          <div class="position-relative material-number-field flex-grow-1">
            <i class="pi pi-search material-search-icon"></i>
            <input v-model="formData.service" type="text" class="form-control"
              placeholder="Search Service / Description / Keyword" />
            <i v-if="formData.service" class="pi pi-times material-clear-icon"
              @click="formData.service = ''"></i>
          </div>
          <div class="position-relative">
            <ButtonPrime v-tooltip.bottom="allFieldsVisible ? 'Hide All Fields' : 'Show All Fields'" :icon="allFieldsVisible ? 'pi pi-eye' : 'pi pi-eye-slash'" size="normal" raised
              rounded
              :severity="allFieldsVisible ? 'primary' : 'secondary'"
              :aria-label="allFieldsVisible ? 'Hide All Fields' : 'Show All Fields'" @click="toggleFieldFilter" />
          </div>
        </div>
      </div>
    </div>

    <!-- Basic Data -->
    <div v-if="isGroupVisible('Basic Data')" class="form-section mb-2">
      <div class="form-section-title">Basic Data</div>
      <div class="row">
        <div v-if="visibleFields.includes('item_name')" class="col-md-4 mb-3">
          <AutoComplete
:model-value="getItemNameDisplayValue()" :suggestions="itemNameSuggestions"
            placeholder="Enter Item Name (INC)" fluid @update:model-value="onItemNameInput"
            @complete="searchItemNames($event.query)" @item-select="onItemNameSelect" />
        </div>
        <div v-if="visibleFields.includes('service_category')" class="col-md-4 mb-3">
          <input
v-model="formData.service_category" type="text" class="form-control"
            placeholder="Enter Service Category" />
        </div>
        <div v-if="visibleFields.includes('service_group')" class="col-md-4 mb-3">
          <input
v-model="formData.service_group" type="text" class="form-control"
            placeholder="Enter Service Group" />
        </div>
        <div v-if="visibleFields.includes('uom')" class="col-md-4 mb-3">
          <AutoComplete
:model-value="getUomDisplayValue()" :suggestions="uomSuggestions" placeholder="Enter UOM (ex: EA - each)"
            fluid @update:model-value="onUomInput" @complete="searchUoms($event.query)"
            @item-select="onUomSelect" />
        </div>
        <div v-if="visibleFields.includes('valuation_class')" class="col-md-4 mb-3">
          <AutoComplete
            :model-value="getValuationClassDisplayValue()" :suggestions="valuationClassSuggestions"
            placeholder="Select Valuation Class" fluid @update:model-value="onValuationClassInput"
            @complete="searchValuationClasses($event.query)" @item-select="onValuationClassSelect" />
        </div>
        <div v-if="visibleFields.includes('group_class')" class="col-md-4 mb-3">
          <AutoComplete
            :model-value="getGroupClassDisplayValue()" :suggestions="groupClassSuggestions"
            placeholder="Enter Group Class" fluid @update:model-value="onGroupClassInput"
            @complete="searchGroupClasses($event.query)" @item-select="onGroupClassSelect" />
        </div>
      </div>
    </div>

    <!-- Other -->
    <div v-if="isGroupVisible('Other')" class="form-section mb-2">
      <div class="form-section-title">Other</div>
      <div class="row">
        <div v-if="visibleFields.includes('mcr_number')" class="col-md-4 mb-3">
          <input v-model="formData.mcr_number" type="text" class="form-control" placeholder="Enter MCR Number" />
        </div>
        <div v-if="visibleFields.includes('created_at')" class="col-md-4 mb-3">
          <input v-model="formData.created_at" type="date" class="form-control" placeholder="Select Created At" />
        </div>
      </div>
    </div>

    <div class="row justify-content-center">
      <div class="col">
        <button type="submit" class="btn btn-primary btn-sm w-100" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ loading ? 'Searching...' : 'Search' }}
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { AutoComplete } from "primevue/autocomplete";
import generalMasterDataService from "@/services/generalMasterData/generalMasterDataService";
import standarItemNameService from '@/services/dictionary/standarItemNameService';
import natoGroupService from "@/services/dictionary/natoGroupService";
import { initialFormData } from './serviceSearchFields';

export default {
  name: 'ServiceSearchForm',
  components: {
    AutoComplete,
  },
  props: {
    initialData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit'],
  data() {
    return {
      formData: { ...initialFormData, ...(this.initialData || {}) },
      fieldGroups: [
        {
          title: 'Basic Data',
          fields: [
            { key: 'item_name', label: 'Item Name' },
            { key: 'service_category', label: 'Service Category' },
            { key: 'service_group', label: 'Service Group' },
            { key: 'uom', label: 'UOM' },
            { key: 'valuation_class', label: 'Valuation Class' },
            { key: 'group_class', label: 'Group Class' },
          ],
        },
        {
          title: 'Other',
          fields: [
            { key: 'mcr_number', label: 'MCR Number' },
            { key: 'created_at', label: 'Created At' },
          ],
        },
      ],
      visibleFields: [],
      clientCode: "",
      // UOM autocomplete data
      uomSuggestions: [],
      selectedUomObject: null,
      allUomData: [],
      // Item Name autocomplete data
      itemNameSuggestions: [],
      selectedItemNameObject: null,
      allItemNameData: [],
      // Valuation Class autocomplete data
      valuationClassSuggestions: [],
      selectedValuationClassObject: null,
      allValuationClassData: [],
      // Group Class (NATO) autocomplete data
      groupClassSuggestions: [],
      selectedGroupClassObject: null,
      allGroupClassData: [],
    };
  },
  computed: {
    totalFieldCount() {
      return this.fieldGroups.reduce((sum, g) => sum + g.fields.length, 0);
    },
    allFieldsVisible() {
      return this.visibleFields.length === this.totalFieldCount;
    },
  },
  watch: {
    initialData: {
      deep: true,
      handler(val) {
        this.formData = { ...initialFormData, ...(val || {}) };
        this.resetSelectedObjects();
      },
    },
  },
  mounted() {
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.clientCode = userAuth?.user?.client_code || '';
  },
  methods: {
    toggleFieldFilter() {
      if (this.allFieldsVisible) {
        this.visibleFields = [];
      } else {
        this.visibleFields = this.fieldGroups.flatMap(g => g.fields.map(f => f.key));
      }
    },

    // Cek apakah sebuah grup punya minimal 1 field yang tampil
    isGroupVisible(title) {
      const group = this.fieldGroups.find(g => g.title === title);
      if (!group) return false;
      return group.fields.some(f => this.visibleFields.includes(f.key));
    },

    // Bangun params bersih (drop nilai kosong/null) dari formData
    buildParams() {
      const params = { ...this.formData };
      // Pakai code UOM yang dipilih jika ada, bukan label tampilan
      if (this.selectedUomObject && this.selectedUomObject.code) {
        params.uom = this.selectedUomObject.code;
      }
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] == null) delete params[key];
      });
      return params;
    },

    // Submit form -> emit cleaned params ke parent
    onSubmit() {
      const params = this.buildParams();
      this.$emit('submit', params);
    },

    // Reset selected object autocomplete (display jatuh ke raw value)
    resetSelectedObjects() {
      this.selectedUomObject = null;
      this.selectedItemNameObject = null;
      this.selectedValuationClassObject = null;
      this.selectedGroupClassObject = null;
    },

    // Get display value for Valuation Class autocomplete
    getValuationClassDisplayValue() {
      if (this.selectedValuationClassObject && this.selectedValuationClassObject.label) {
        return this.selectedValuationClassObject.label;
      }
      return this.formData.valuation_class || '';
    },

    // Search Valuation Classes for autocomplete
    async searchValuationClasses(query) {
      if (!query || query.length < 2) {
        this.valuationClassSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('valuation-classes', { search: query, limit: 100, client: this.clientCode });
        const listData = response.data && response.data.data;
        const items = Array.isArray(listData) ? listData : (listData && Array.isArray(listData.data) ? listData.data : []);

        if (items.length > 0) {
          this.allValuationClassData = items;
          this.valuationClassSuggestions = items.map(vc => vc.label);
        } else {
          this.valuationClassSuggestions = [];
          this.allValuationClassData = [];
        }
      } catch (error) {
        console.error('Error searching valuation classes:', error);
        this.valuationClassSuggestions = [];
        this.allValuationClassData = [];
      }
    },

    // Handle Valuation Class input (when user types manually)
    onValuationClassInput(value) {
      const matchingValuationClass = this.allValuationClassData.find(vc => vc.label === value);
      if (matchingValuationClass) {
        this.selectedValuationClassObject = matchingValuationClass;
        this.formData.valuation_class = matchingValuationClass.code; // Store the code when selected from suggestions
      } else {
        this.selectedValuationClassObject = null;
        this.formData.valuation_class = value;
      }
    },

    // Handle Valuation Class selection from autocomplete dropdown
    onValuationClassSelect(event) {
      if (event.value) {
        const matchingValuationClass = this.allValuationClassData.find(vc => vc.label === event.value);
        if (matchingValuationClass) {
          this.selectedValuationClassObject = matchingValuationClass;
          this.formData.valuation_class = matchingValuationClass.code; // Store the code when selected
        }
      }
    },

    // Get display value for item name autocomplete
    getItemNameDisplayValue() {
      if (this.selectedItemNameObject && this.selectedItemNameObject.label) {
        return this.selectedItemNameObject.label;
      }
      return this.formData.item_name || '';
    },

    // Search item names for autocomplete
    async searchItemNames(query) {
      if (!query || query.length < 2) {
        this.itemNameSuggestions = [];
        return;
      }

      try {
        const response = await standarItemNameService.list({ search: query, limit: 100 });
        if (response.data && response.data.data && response.data.data.data) {
          this.allItemNameData = response.data.data.data;
          this.itemNameSuggestions = response.data.data.data.map(item => item.label);
        } else {
          this.itemNameSuggestions = [];
          this.allItemNameData = [];
        }
      } catch (error) {
        console.error('Error searching item names:', error);
        this.itemNameSuggestions = [];
        this.allItemNameData = [];
      }
    },

    // Handle item name input (when user types manually)
    onItemNameInput(value) {
      const matchingItem = this.allItemNameData.find(item => item.label === value);
      if (matchingItem) {
        this.selectedItemNameObject = matchingItem;
        this.formData.item_name = matchingItem.inc; // Store the INC when selected from suggestions
      } else {
        this.selectedItemNameObject = null;
        this.formData.item_name = value;
      }
    },

    // Handle item name selection from autocomplete dropdown
    onItemNameSelect(event) {
      if (event.value) {
        const matchingItem = this.allItemNameData.find(item => item.label === event.value);
        if (matchingItem) {
          this.selectedItemNameObject = matchingItem;
          this.formData.item_name = matchingItem.inc; // Store the INC when selected
        }
      }
    },

    // Get display value for UOM autocomplete
    getUomDisplayValue() {
      // If we have a selected UOM object, show its label
      if (this.selectedUomObject && this.selectedUomObject.label) {
        return this.selectedUomObject.label;
      }
      // Otherwise, show the raw value from formData.uom
      return this.formData.uom || '';
    },

    // Search UOMs for autocomplete
    async searchUoms(query) {
      if (!query || query.length < 2) {
        this.uomSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('uom', { search: query, limit: 100, client: this.clientCode });
        if (response.data && response.data.data && response.data.data.data) {
          this.allUomData = response.data.data.data;
          this.uomSuggestions = response.data.data.data.map(uom => uom.label);
        } else {
          this.uomSuggestions = [];
          this.allUomData = [];
        }
      } catch (error) {
        console.error('Error searching UOMs:', error);
        this.uomSuggestions = [];
        this.allUomData = [];
      }
    },

    // Handle UOM input (when user types manually)
    onUomInput(value) {
      // If value is one of our UOM suggestions, find the matching object
      const matchingUom = this.allUomData.find(uom => uom.label === value);
      if (matchingUom) {
        this.selectedUomObject = matchingUom;
        this.formData.uom = matchingUom.code; // Store the code when selected from suggestions
      } else {
        // If user types manually, clear the selected object and store the typed value
        this.selectedUomObject = null;
        this.formData.uom = value;
      }
    },

    // Handle UOM selection from autocomplete dropdown
    onUomSelect(event) {
      if (event.value) {
        // Find the matching UOM in allUomData based on the selected label
        const matchingUom = this.allUomData.find(uom => uom.label === event.value);
        if (matchingUom) {
          this.selectedUomObject = matchingUom;
          this.formData.uom = matchingUom.code; // Store the code when selected
        }
      }
    },

    // Get display value for Group Class (NATO) autocomplete
    getGroupClassDisplayValue() {
      if (this.selectedGroupClassObject && this.selectedGroupClassObject.label) {
        return this.selectedGroupClassObject.label;
      }
      return this.formData.group_class || '';
    },

    // Search Group Classes (NATO) for autocomplete
    async searchGroupClasses(query) {
      if (!query || query.length < 2) {
        this.groupClassSuggestions = [];
        return;
      }

      try {
        const response = await natoGroupService.list({ search: query, limit: 100 });
        const items = response.data?.data?.data || [];
        if (Array.isArray(items) && items.length > 0) {
          // Build label "nsc - description"; keep nsc for the value
          this.allGroupClassData = items.map(item => ({
            ...item,
            label: `${item.nsc} - ${item.description}`,
          }));
          this.groupClassSuggestions = this.allGroupClassData.map(item => item.label);
        } else {
          this.groupClassSuggestions = [];
          this.allGroupClassData = [];
        }
      } catch (error) {
        console.error('Error searching group classes:', error);
        this.groupClassSuggestions = [];
        this.allGroupClassData = [];
      }
    },

    // Handle Group Class input (when user types manually)
    onGroupClassInput(value) {
      const matchingGroupClass = this.allGroupClassData.find(item => item.label === value);
      if (matchingGroupClass) {
        this.selectedGroupClassObject = matchingGroupClass;
        this.formData.group_class = matchingGroupClass.nsc; // Store the NSC when selected from suggestions
      } else {
        this.selectedGroupClassObject = null;
        this.formData.group_class = value;
      }
    },

    // Handle Group Class selection from autocomplete dropdown
    onGroupClassSelect(event) {
      if (event.value) {
        const matchingGroupClass = this.allGroupClassData.find(item => item.label === event.value);
        if (matchingGroupClass) {
          this.selectedGroupClassObject = matchingGroupClass;
          this.formData.group_class = matchingGroupClass.nsc; // Store the NSC when selected
        }
      }
    },
  },
};
</script>

<style scoped>
/* Standardize size for inputs, autocomplete, and selects within search form */
.search-form .form-control,
.search-form .form-select {
  height: 44px;
  line-height: 1.25;
  font-size: 14px !important;
  font-family: inherit;
  padding: 8px 12px;
  box-sizing: border-box;
}

/* PrimeVue input inside AutoComplete */
.search-form :deep(.p-inputtext) {
  height: 44px;
  line-height: 1.25;
  font-size: 14px !important;
  font-family: inherit;
  padding: 8px 12px;
  width: 100%;
  box-sizing: border-box;
}

/* Ensure wrapper of AutoComplete inherits consistent font size */
.search-form :deep(.p-autocomplete) {
  font-size: 14px !important;
  font-family: inherit;
}

/* Placeholder sizing and color */
.search-form .form-control::placeholder,
.search-form :deep(.p-inputtext::placeholder) {
  font-size: 14px;
  color: #6c757d;
  opacity: 1;
}

/* Service Number field with search & clear icons */
.material-number-field {
  display: flex;
  align-items: center;
}

.material-number-field .form-control {
  padding-left: 40px;
  padding-right: 40px;
}

.material-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 16px;
  pointer-events: none;
}

.material-clear-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 14px;
  cursor: pointer;
}

.material-clear-icon:hover {
  color: #dc3545;
}

/* Form section grouping */
.form-section-title {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 4px;
  margin-bottom: 12px;
}
</style>
